<template>
  <div class="map-wrap">
    <div ref="container" class="map-container">
      <div v-if="loading" class="loading">加载中...</div>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { ThreeUtils } from "@/utils/threeUtils";

let scene, camera, renderer, controls;
let earth, stars, earthRotatingGroup, flatEarth, flatFlowLines;
let animationId = null;
let signalIntervalId = null;
let animations = [];

const earthRadius = 5;

// 默认配色：基础底线（静态淡线）/ 点位标记、动态飞线
const BASE_LINE_COLOR = 0x00e5ff;
const DEFAULT_POINT_COLOR = 0xff2d9b;

const cameraConfig = {
  fov: 45,
  near: 0.1,
  far: 1000,
  initialDistance: 20,
};

export default {
  name: "Map",
  props: {
    projectList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: true,
      animationEnabled: true,
      earthRotating: true,
      viewMode: "3d",
    };
  },
  mounted() {
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    this.cleanupScene();
  },
  watch: {
    projectList: {
      handler(newVal) {
        // 项目数据异步到达后，存在含有效经纬度的真实项目时重建飞线
        const hasValidPoint = (newVal || []).some((item) =>
          Number.isFinite(Number(item.latitude)) &&
          Number.isFinite(Number(item.longitude))
        );
        if (hasValidPoint && earthRotatingGroup) {
          this.rebuildFlyLines();
        }
      },
    },
  },
  methods: {
    // 选择项目：停止地球自转，相机飞行定位到项目位置（3D/2D 通用）
    selectProject(project) {
      if (!project) return;

      // 选择项目后停止地球自转
      this.earthRotating = false;

      // 解析经纬度：优先用入参自带坐标，其次按 code/name 在 projectList 中匹配
      let lat = Number(project.latitude);
      let lon = Number(project.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        const matched = (this.projectList || []).find(
          (p) => p.code === project.code || p.name === project.name
        );
        if (matched) {
          lat = Number(matched.latitude);
          lon = Number(matched.longitude);
        }
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      if (this.viewMode === "2d") {
        this.flyToFlat(lat, lon);
      } else {
        this.flyToGlobe(lat, lon);
      }
    },

    // 3D 球面视角飞行：考虑自转组当前旋转，取项目点世界坐标后从外侧观察
    flyToGlobe(lat, lon) {
      if (!earthRotatingGroup) return;
      earthRotatingGroup.updateWorldMatrix(true, false);

      const localPos = this.latLonToVector3(lat, lon, earthRadius);
      const worldPos = earthRotatingGroup.localToWorld(localPos.clone());
      // 相机停在项目点沿径向往外的位置（需大于 OrbitControls.minDistance=8）
      const viewDistance = 9;
      const endPos = worldPos.clone().normalize().multiplyScalar(earthRadius + viewDistance);
      // target 保持地心：项目点位于相机-地心连线上，自然落在视野中心；
      // 同时保证拖拽始终绕地心旋转，不会偏离地球自转中心
      const endTarget = new THREE.Vector3(0, 0, 0);

      this.animateCameraTo(endPos, endTarget, 1.2);
    },

    // 2D 平面视角飞行：正交相机平移到项目点上方
    flyToFlat(lat, lon) {
      const p = this.latLonToFlat(lat, lon);
      const endPos = new THREE.Vector3(p.x, p.y, 35);
      const endTarget = new THREE.Vector3(p.x, p.y, 0);
      this.animateCameraTo(endPos, endTarget, 1.0);
    },

    // 相机 + 控制器目标点的缓动飞行（easeInOutCubic），重复点击以当前视角重新起飞
    animateCameraTo(endPos, endTarget, duration = 1.2) {
      if (!camera || !controls) return;

      // 移除上一次未完成的飞行动画
      for (let i = animations.length - 1; i >= 0; i--) {
        if (animations[i].name === "cameraFlyAnimation") animations.splice(i, 1);
      }

      const startPos = camera.position.clone();
      const startTarget = controls.target.clone();
      const startTime = performance.now();
      const easeInOutCubic = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const tween = {
        name: "cameraFlyAnimation",
        update() {
          const t = Math.min(1, (performance.now() - startTime) / 1000 / duration);
          const k = easeInOutCubic(t);
          camera.position.lerpVectors(startPos, endPos, k);
          controls.target.lerpVectors(startTarget, endTarget, k);
          if (t >= 1) {
            const idx = animations.indexOf(tween);
            if (idx > -1) animations.splice(idx, 1);
          }
        },
      };
      animations.push(tween);
    },
    // 创建地球光圈
    initEarthSprite(group) {
      const texture = new THREE.TextureLoader().load("/textures/地球光圈.png");
      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(earthRadius * 3, earthRadius * 3, 1);
      sprite.name = "EarthSpriteGlow";
      group.add(sprite);
    },

    // 经纬度转球面三维坐标
    latLonToVector3(lat, lon, radius) {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lon + 180) * (Math.PI / 180);
      const x = -(radius * Math.sin(phi) * Math.cos(theta));
      const z = radius * Math.sin(phi) * Math.sin(theta);
      const y = radius * Math.cos(phi);
      return new THREE.Vector3(x, y, z);
    },

    // 颜色值归一化：支持 "#ef825b" / 0xef825b，非法值回退默认色
    normalizeColor(color, fallback = DEFAULT_POINT_COLOR) {
      if (typeof color === "number") return color;
      if (typeof color === "string" && /^#?[0-9a-fA-F]{6}$/.test(color.trim())) {
        return new THREE.Color(color.trim()).getHex();
      }
      return fallback;
    },

    // 点位 + 飞线数据（3D/2D 通用）：内置模拟城市 + projectList 真实项目点位融合
    // 返回 { points, connections }
    //   points:      [{ name, code?, lat, lon, color, isProject? }]
    //   connections: [{ from: 起点索引, to: 终点索引, color: 飞线颜色 }]
    getMockFlyLineData() {
      // 真实项目接入的枢纽城市索引：北京、迪拜
      const hubIndexes = [0, 6];

      // 1. 内置模拟城市点位（默认点位色）
      const cities = [
        { name: "北京", lat: 39.9, lon: 116.4, color: DEFAULT_POINT_COLOR },
        { name: "上海", lat: 31.2, lon: 121.5, color: DEFAULT_POINT_COLOR },
        { name: "纽约", lat: 40.7, lon: -74.0, color: DEFAULT_POINT_COLOR },
        { name: "伦敦", lat: 51.5, lon: -0.1, color: DEFAULT_POINT_COLOR },
        { name: "东京", lat: 35.7, lon: 139.7, color: DEFAULT_POINT_COLOR },
        { name: "悉尼", lat: -33.9, lon: 151.2, color: DEFAULT_POINT_COLOR },
        { name: "迪拜", lat: 25.2, lon: 55.3, color: DEFAULT_POINT_COLOR },
        { name: "新加坡", lat: 1.35, lon: 103.8, color: DEFAULT_POINT_COLOR },
        { name: "莫斯科", lat: 55.8, lon: 37.6, color: DEFAULT_POINT_COLOR },
        { name: "洛杉矶", lat: 34.1, lon: -118.2, color: DEFAULT_POINT_COLOR },
        { name: "开罗", lat: 30.0, lon: 31.2, color: DEFAULT_POINT_COLOR },
        { name: "圣保罗", lat: -23.5, lon: -46.6, color: DEFAULT_POINT_COLOR },
      ];
      // 2. 融合真实项目点位（经纬度无效的数据过滤掉，颜色取接口返回值）
      const projectPoints = (this.projectList || [])
        .map((item) => {
          const lat = Number(item.latitude);
          const lon = Number(item.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          return {
            name: item.name,
            code: item.code,
            lat,
            lon,
            color: this.normalizeColor(item.color, DEFAULT_POINT_COLOR),
            isProject: true,
          };
        })
        .filter(Boolean);

      const points = [...cities, ...projectPoints];

      // 3. 模拟城市之间的飞线（默认飞线色）
      const connections = [
        [0, 1], [0, 3], [0, 4], [0, 6],
        [1, 4], [1, 7],
        [2, 3], [2, 9], [2, 11],
        [3, 8], [3, 6],
        [4, 7],
        [5, 7], [5, 4],
        [6, 7], [6, 10],
        [8, 6], [8, 0],
        [9, 2],
        [10, 11], [10, 6],
      ].map(([from, to]) => ({ from, to, color: DEFAULT_POINT_COLOR }));

      // 4. 真实项目飞线：每个项目接入北京/迪拜枢纽，项目之间两两相连；颜色取项目色
      projectPoints.forEach((project, i) => {
        const projectIdx = cities.length + i;
        hubIndexes.forEach((hubIdx) => {
          connections.push({ from: projectIdx, to: hubIdx, color: project.color });
        });
      });
      for (let i = 0; i < projectPoints.length; i++) {
        for (let j = i + 1; j < projectPoints.length; j++) {
          connections.push({
            from: cities.length + i,
            to: cities.length + j,
            color: projectPoints[i].color,
          });
        }
      }

      return { points, connections };
    },

    // 创建数据流线条
    createDataFlowLines(group) {
      const flowLinesGroup = new THREE.Group();
      flowLinesGroup.name = "DataFlowLines";
      group.add(flowLinesGroup);

      const { points, connections } = this.getMockFlyLineData();

      const flowHeads = [];
      const flowMaterials = [];
      const nodeRings = [];
      const self = this;

      connections.forEach(({ from: fromIdx, to: toIdx, color: lineColor }) => {
        const from = points[fromIdx];
        const to = points[toIdx];
        const lineName = `${from.name}-${to.name}`;
        const start = self.latLonToVector3(from.lat, from.lon, earthRadius);
        const end = self.latLonToVector3(to.lat, to.lon, earthRadius);

        const mid = start.clone().add(end).multiplyScalar(0.5);
        const dist = start.distanceTo(end);
        const liftHeight = earthRadius * 0.2 + dist * 0.12;
        const control = mid.clone().normalize().multiplyScalar(earthRadius + liftHeight);

        const curve = new THREE.QuadraticBezierCurve3(start, control, end);
        const curvePoints = curve.getPoints(80);

        // 1. 基础弧线（静态底线，固定默认色）
        const baseGeom = new THREE.BufferGeometry().setFromPoints(curvePoints);
        const baseMat = new THREE.LineBasicMaterial({
          color: BASE_LINE_COLOR,
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
        });
        const baseLine = new THREE.Line(baseGeom, baseMat);
        flowLinesGroup.add(baseLine);

        // 2. 流动亮带 Shader 线
        const distances = new Float32Array(curvePoints.length);
        let totalLen = 0;
        for (let i = 1; i < curvePoints.length; i++) {
          totalLen += curvePoints[i].distanceTo(curvePoints[i - 1]);
          distances[i] = totalLen;
        }
        const flowGeom = new THREE.BufferGeometry().setFromPoints(curvePoints);
        flowGeom.setAttribute("distance", new THREE.BufferAttribute(distances, 1));

        const flowMat = new THREE.ShaderMaterial({
          uniforms: {
            time: { value: 0 },
            totalLength: { value: totalLen },
            color: { value: new THREE.Color(lineColor) },
          },
          name: lineName,
          vertexShader: `
            attribute float distance;
            varying float vDistance;
            void main() {
              vDistance = distance;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform float time;
            uniform float totalLength;
            uniform vec3 color;
            varying float vDistance;
            void main() {
              float t = vDistance / totalLength;
              float flow = fract(t - time * 0.35);
              float pulse = smoothstep(0.0, 0.04, flow) * (1.0 - smoothstep(0.04, 0.18, flow));
              gl_FragColor = vec4(color, pulse);
            }
          `,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        flowMaterials.push(flowMat);
        const flowLine = new THREE.Line(flowGeom, flowMat);
        flowLine.visible = lineName === "北京-上海";
        flowLinesGroup.add(flowLine);

        // 3. 流动线段头部
        const headSegmentCount = 16;
        const headPositions = new Float32Array(headSegmentCount * 3);
        const headGeom = new THREE.BufferGeometry();
        headGeom.setAttribute(
          "position",
          new THREE.BufferAttribute(headPositions, 3)
        );
        const headMat = new THREE.LineBasicMaterial({
          color: lineColor,
          transparent: true,
          opacity: 0.95,
        });
        const head = new THREE.Line(headGeom, headMat);
        head.userData = {
          name: lineName,
          curve,
          progress: 0,
          speed: 0.5,
          halfWidth: 0.04,
          segmentCount: headSegmentCount,
          active: false,
          elapsed: 0,
          duration: 2,
        };
        head.visible = false;
        flowHeads.push(head);
        flowLinesGroup.add(head);
      
      }); 

      return flowLinesGroup;
    },

    // 经纬度转 2D 平面坐标（等距圆柱投影）
    latLonToFlat(lat, lon) {
      const flatWidth = earthRadius * 2 * Math.PI;
      const flatHeight = earthRadius * 2;
      const x = (lon / 180) * (flatWidth / 2);
      const y = (lat / 90) * (flatHeight / 2);
      return new THREE.Vector3(x, y, 0.1);
    },

    // 创建 2D 平面数据流线条
    createFlatFlowLines() {
      const flowLinesGroup = new THREE.Group();
      flowLinesGroup.name = "FlatFlowLines";

      const { points, connections } = this.getMockFlyLineData();

      const flowHeads = [];
      const flowMaterials = [];
      const self = this;

      connections.forEach(({ from: fromIdx, to: toIdx, color: lineColor }) => {
        const from = points[fromIdx];
        const to = points[toIdx];
        const lineName = `${from.name}-${to.name}`;
        const start = self.latLonToFlat(from.lat, from.lon);
        const end = self.latLonToFlat(to.lat, to.lon);

        // 平面上的弧线控制点
        const mid = start.clone().add(end).multiplyScalar(0.5);
        const dist = start.distanceTo(end);
        mid.y += dist * 0.15;
        const control = mid;

        const curve = new THREE.QuadraticBezierCurve3(start, control, end);
        const curvePoints = curve.getPoints(80);

        // 1. 基础线（静态底线，固定默认色）
        const baseGeom = new THREE.BufferGeometry().setFromPoints(curvePoints);
        const baseMat = new THREE.LineBasicMaterial({
          color: BASE_LINE_COLOR,
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
        });
        const baseLine = new THREE.Line(baseGeom, baseMat);
        flowLinesGroup.add(baseLine);

        // 2. 流动亮带 Shader 线
        const distances = new Float32Array(curvePoints.length);
        let totalLen = 0;
        for (let i = 1; i < curvePoints.length; i++) {
          totalLen += curvePoints[i].distanceTo(curvePoints[i - 1]);
          distances[i] = totalLen;
        }
        const flowGeom = new THREE.BufferGeometry().setFromPoints(curvePoints);
        flowGeom.setAttribute("distance", new THREE.BufferAttribute(distances, 1));

        const flowMat = new THREE.ShaderMaterial({
          uniforms: {
            time: { value: 0 },
            totalLength: { value: totalLen },
            color: { value: new THREE.Color(lineColor) },
          },
          name: lineName,
          vertexShader: `
            attribute float distance;
            varying float vDistance;
            void main() {
              vDistance = distance;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform float time;
            uniform float totalLength;
            uniform vec3 color;
            varying float vDistance;
            void main() {
              float t = vDistance / totalLength;
              float flow = fract(t - time * 0.35);
              float pulse = smoothstep(0.0, 0.04, flow) * (1.0 - smoothstep(0.04, 0.18, flow));
              gl_FragColor = vec4(color, pulse);
            }
          `,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        flowMaterials.push(flowMat);
        const flowLine = new THREE.Line(flowGeom, flowMat);
        flowLine.visible = lineName === "北京-上海";
        flowLinesGroup.add(flowLine);

        // 3. 流动线段头部
        const headSegmentCount = 16;
        const headPositions = new Float32Array(headSegmentCount * 3);
        const headGeom = new THREE.BufferGeometry();
        headGeom.setAttribute(
          "position",
          new THREE.BufferAttribute(headPositions, 3)
        );
        const headMat = new THREE.LineBasicMaterial({
          color: lineColor,
          transparent: true,
          opacity: 0.95,
        });
        const head = new THREE.Line(headGeom, headMat);
        head.userData = {
          name: lineName,
          curve,
          progress: 0,
          speed: 0.5,
          halfWidth: 0.04,
          segmentCount: headSegmentCount,
          active: false,
          elapsed: 0,
          duration: 2,
        };
        head.visible = false;
        flowHeads.push(head);
        flowLinesGroup.add(head);

        // 4. 节点标记（仅实心小圆点，颜色与各自点位一致）
        const dotRadius = 0.05;
        const dotGeom = new THREE.CircleGeometry(dotRadius, 24);

        [
          { pos: start, color: from.color },
          { pos: end, color: to.color },
        ].forEach(({ pos, color }) => {
          const dotMat = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.95,
            side: THREE.DoubleSide,
          });
          const dot = new THREE.Mesh(dotGeom, dotMat);
          dot.position.set(pos.x, pos.y, 0.2);
          flowLinesGroup.add(dot);
        });
      });

      let lastTime = performance.now();

      const updateFlatFlowLines = () => {
        if (!self.animationEnabled) return;
        const now = performance.now();
        const delta = (now - lastTime) / 1000;
        lastTime = now;

        flowMaterials.forEach((mat) => {
          if (mat.name !== "北京-上海") return;
          mat.uniforms.time.value += delta * 0.72;
        });

        flowHeads.forEach((head) => {
          const data = head.userData;
          if (!data.active) return;

          data.elapsed += delta;
          if (data.elapsed >= data.duration) {
            data.active = false;
            data.elapsed = 0;
            head.visible = false;
            return;
          }

          data.progress += data.speed * delta;
          if (data.progress > 1) data.progress = 0;

          const { curve, progress, halfWidth, segmentCount } = data;
          const tStart = Math.max(0, progress - halfWidth);
          const tEnd = Math.min(1, progress + halfWidth);
          const positionAttr = head.geometry.getAttribute("position");

          for (let i = 0; i < segmentCount; i++) {
            const t = tStart + (tEnd - tStart) * (i / (segmentCount - 1));
            const p = curve.getPoint(t);
            positionAttr.setXYZ(i, p.x, p.y, p.z);
          }
          positionAttr.needsUpdate = true;
          head.geometry.computeBoundingSphere();
        });
      };

      animations.push({
        update: updateFlatFlowLines,
        name: "flatFlowLinesAnimation",
      });

      return flowLinesGroup;
    },

    // 飞线从起点飞向终点后消失 ，光段两端线宽渐细、半透明，中段最粗、颜色加深
    // 飞线颜色由 getMockFlyLineData 的每条连接数据决定（项目色 / 默认 0xff2d9b）
    // options: {
    //   mode: "globe"    显示模式：globe-3D 球面 / flat-2D 平面
    //   lineWidth: 0.03  飞线粗细（管道最大半径）
    //   speed: 0.35      飞行速度（进度/秒）
    //   trailLen: 0.35   飞渡尾迹长度（占整条弧线比例 0~1）
    // }
    createArcFlyLines(options = {}) {
      const {
        mode = "globe",
        lineWidth = 0.015,
        speed = 0.8,
        trailLen = 1,
      } = options;

      const flyLinesGroup = new THREE.Group();
      flyLinesGroup.name = "ArcFlyLines";

      const { points, connections } = this.getMockFlyLineData();

      const flyItems = [];

      connections.forEach(({ from: fromIdx, to: toIdx, color: lineColor }) => {
        const from = points[fromIdx];
        const to = points[toIdx];

        let start, end, control;

        if (mode === "flat") {
          // 2D 平面：弧线控制点中间向上拱起
          start = this.latLonToFlat(from.lat, from.lon);
          end = this.latLonToFlat(to.lat, to.lon);
          const mid = start.clone().add(end).multiplyScalar(0.5);
          const dist = start.distanceTo(end);
          mid.y += dist * 0.15;
          control = mid;
        } else {
          // 3D 球面：控制点沿法线方向抬升形成拱起弧线
          start = this.latLonToVector3(from.lat, from.lon, earthRadius);
          end = this.latLonToVector3(to.lat, to.lon, earthRadius);
          const mid = start.clone().add(end).multiplyScalar(0.5);
          const dist = start.distanceTo(end);
          const liftHeight = earthRadius * 0.2 + dist * 0.12;
          control = mid.clone().normalize().multiplyScalar(earthRadius + liftHeight);
        }

        const curve = new THREE.QuadraticBezierCurve3(start, control, end);

        // 管道几何体：支持自定义飞线粗细，uv.x 沿管道长度方向 0 -> 1
        const tubeSegments = 128;
        const radialSegments = 8;
        const geometry = new THREE.TubeGeometry(
          curve,
          tubeSegments,
          lineWidth,
          radialSegments,
          false
        );

        // 烘焙每个环形分段的中心点，供顶点着色器做两端渐细的锥形效果
        const positionAttr = geometry.getAttribute("position");
        const centers = new Float32Array(positionAttr.count * 3);
        const ringCenter = new THREE.Vector3();
        for (let i = 0; i <= tubeSegments; i++) {
          curve.getPointAt(i / tubeSegments, ringCenter);
          for (let j = 0; j <= radialSegments; j++) {
            const k = i * (radialSegments + 1) + j;
            centers[k * 3] = ringCenter.x;
            centers[k * 3 + 1] = ringCenter.y;
            centers[k * 3 + 2] = ringCenter.z;
          }
        }
        geometry.setAttribute("aCenter", new THREE.BufferAttribute(centers, 3));

        const material = new THREE.ShaderMaterial({
          uniforms: {
            uColor: { value: new THREE.Color(lineColor) },
            uProgress: { value: 0 },
            uTrailLen: { value: trailLen },
          },
          vertexShader: `
            #include <common>
            #include <logdepthbuf_pars_vertex>
            attribute vec3 aCenter;
            uniform float uProgress;
            uniform float uTrailLen;
            varying vec2 vUv;
            void main() {
              vUv = uv;
              float tail = uProgress - uTrailLen;
              // 当前顶点在飞渡窗口内的位置：0(尾) -> 1(头)
              float s = clamp((uv.x - tail) / uTrailLen, 0.0, 1.0);
              // 两端线宽渐细：正弦锥形，中段最粗
              float taper = 0.1 + 0.9 * sin(s * 3.14159265);
              vec3 transformed = aCenter + (position - aCenter) * taper;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
              #include <logdepthbuf_vertex>
            }
          `,
          fragmentShader: `
            #include <common>
            #include <logdepthbuf_pars_fragment>
            uniform vec3 uColor;
            uniform float uProgress;
            uniform float uTrailLen;
            varying vec2 vUv;
            void main() {
              float t = vUv.x;
              float tail = uProgress - uTrailLen;
              // 只显示飞行头部窗口内的光段
              if (t > uProgress || t < tail) discard;
              float s = (t - tail) / uTrailLen;
              // 尾部淡入，头部略微收光；两端半透明、中段颜色加深
              float alpha = smoothstep(0.0, 0.3, s) * (1.0 - smoothstep(0.85, 1.0, s) * 0.5);
              alpha *= 0.45 + 0.55 * sin(s * 3.14159265);
              // 头部提亮形成飞行光点
              vec3 color = mix(uColor, vec3(1.0), smoothstep(0.65, 1.0, s) * 0.55);
              gl_FragColor = vec4(color, alpha);
              #include <logdepthbuf_fragment>
            }
          `,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });

        const mesh = new THREE.Mesh(geometry, material);
        flyLinesGroup.add(mesh);

        const item = {
          material,
          // 初始随机进度，页面加载后立即有效果
          progress: Math.random() * (1 + trailLen),
          delay: Math.random() * 1.2,
        };
        material.uniforms.uProgress.value = item.progress;
        flyItems.push(item);
      });

      // 节点圆环 - 节点脉冲圆环效果（节点环）
      const nodeRings = [];
      const cityIndices = [...new Set(connections.flatMap((c) => [c.from, c.to]))];
      const ringGeom = new THREE.TorusGeometry(0.06, 0.006, 8, 32);
      const ringCount = 8;

      cityIndices.forEach((cityIdx) => {
        const city = points[cityIdx];
        let base, normal, riseHeight;

        if (mode === "flat") {
          // 2D 平面：圆环正对相机，原地扩散
          base = this.latLonToFlat(city.lat, city.lon);
          base.z = 0.2;
          normal = new THREE.Vector3(0, 0, 1);
          riseHeight = 0;
        } else {
          // 3D 球面：圆环沿法线贴球面，向外浮起扩散
          base = this.latLonToVector3(city.lat, city.lon, earthRadius);
          normal = base.clone().normalize();
          riseHeight = 0.3;
        }

        for (let i = 0; i < ringCount; i++) {
          const ringMat = new THREE.MeshBasicMaterial({
            color: new THREE.Color(city.color),
            transparent: true,
            opacity: 0,
            depthWrite: false,
            side: THREE.DoubleSide,
          });
          const ring = new THREE.Mesh(ringGeom, ringMat);
          ring.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
          ring.userData = { base, normal, riseHeight, phase: i / ringCount };
          nodeRings.push(ring);
          flyLinesGroup.add(ring);
        }
      });

      const self = this;
      let lastTime = performance.now();

      const updateArcFlyLines = () => {
        if (!self.animationEnabled) return;
        const now = performance.now();
        const delta = (now - lastTime) / 1000;
        lastTime = now;

        flyItems.forEach((item) => {
          // 起飞延迟，错开各条飞线
          if (item.delay > 0) {
            item.delay -= delta;
            return;
          }

          // 飞渡阶段：头部从 0 飞到 1 + 尾长，尾迹完全滑出终点即消失
          item.progress += speed * delta;
          if (item.progress > 1 + trailLen) {
            // 重置进入下一轮飞行
            item.progress = 0;
            item.delay = Math.random() * 1.5;
          }
          item.material.uniforms.uProgress.value = item.progress;
        });

        // 节点脉冲圆环：循环浮起/扩散 + 淡出
        const ringTime = performance.now() / 1000;
        nodeRings.forEach((ring) => {
          const { base, normal, riseHeight, phase } = ring.userData;
          const t = (ringTime * 0.5 + phase) % 1;
          ring.position
            .copy(base)
            .add(normal.clone().multiplyScalar(riseHeight * t));
          const scale = 0.6 + 0.7 * t;
          ring.scale.set(scale, scale, scale);
          ring.material.opacity = 0.9 * (1.0 - t);
        });
      };

      animations.push({
        update: updateArcFlyLines,
        name: "arcFlyLinesAnimation",
      });

      return flyLinesGroup;
    },

    // 递归释放组内所有几何体与材质
    disposeObjectGroup(group) {
      if (!group) return;
      group.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((mat) => mat.dispose());
        }
      });
    },

    // 真实项目数据到达后，按最新点位重建 3D 球面 + 2D 平面全部飞线
    rebuildFlyLines() {
      if (!earthRotatingGroup || !earth) return;

      // 1. 移除旧飞线动画（arcFlyLinesAnimation 存在 3D/2D 两个实例）
      const animationNames = ["flatFlowLinesAnimation", "arcFlyLinesAnimation"];
      for (let i = animations.length - 1; i >= 0; i--) {
        if (animationNames.includes(animations[i].name)) {
          animations.splice(i, 1);
        }
      }

      // 2. 移除并释放旧飞线组
      const removeGroup = (parent, name) => {
        const oldGroup = parent.getObjectByName(name);
        if (oldGroup) {
          this.disposeObjectGroup(oldGroup);
          parent.remove(oldGroup);
        }
      };
      removeGroup(earthRotatingGroup, "DataFlowLines");
      removeGroup(earthRotatingGroup, "ArcFlyLines");
      if (flatFlowLines) {
        this.disposeObjectGroup(flatFlowLines);
        earth.remove(flatFlowLines);
        flatFlowLines = null;
      }

      // 3. 使用最新 getMockFlyLineData 重建
      this.createDataFlowLines(earthRotatingGroup);
      earthRotatingGroup.add(this.createArcFlyLines());

      flatFlowLines = this.createFlatFlowLines();
      flatFlowLines.add(this.createArcFlyLines({ mode: "flat", lineWidth: 0.01 }));
      flatFlowLines.visible = this.viewMode === "2d";
      earth.add(flatFlowLines);
    },

    createEarth() {
      const existing = scene.getObjectByName("EarthContainer");
      if (existing) scene.remove(existing);

      earth = new THREE.Group();
      earth.name = "EarthContainer";
      scene.add(earth);

      const textureLoader = new THREE.TextureLoader();

      earthRotatingGroup = new THREE.Group();
      earthRotatingGroup.name = "EarthRotatingGroup";
      earth.add(earthRotatingGroup);

      // 3D 球体
      const earthGeometry = new THREE.SphereGeometry(earthRadius, 64, 64);
      const earthMaterial = new THREE.MeshPhongMaterial({
        map: textureLoader.load("/textures/earth2.jpg"),
        emissiveIntensity: 0.5,
        side: THREE.DoubleSide,
      });
      const earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
      earthMesh.name = "EarthMesh";
      earthRotatingGroup.add(earthMesh);

      this.initEarthSprite(earthRotatingGroup);
      this.createDataFlowLines(earthRotatingGroup);

      // 弧线飞线效果（颜色、粗细可传参）：3D 球面版，随地球自转，默认视图可见
      earthRotatingGroup.add(
        this.createArcFlyLines()
      );

      // 2D 平面地图（equirectangular 贴图直接铺到平面）
      const flatWidth = earthRadius * 2 * Math.PI;
      const flatHeight = earthRadius * 2;
      const flatGeometry = new THREE.PlaneGeometry(flatWidth, flatHeight, 64, 32);
      const flatMaterial = new THREE.MeshPhongMaterial({
        map: textureLoader.load("/textures/earth2.jpg"),
        side: THREE.DoubleSide,
      });
      flatEarth = new THREE.Mesh(flatGeometry, flatMaterial);
      flatEarth.name = "FlatEarth";
      flatEarth.visible = false;
      earth.add(flatEarth);

      // 2D 平面数据流效果
      flatFlowLines = this.createFlatFlowLines();
      flatFlowLines.visible = false;
      earth.add(flatFlowLines);

      // 弧线飞线效果：2D 平面版，挂载到平面流线组，随 2D 视图显隐
      flatFlowLines.add(
        this.createArcFlyLines({ mode: "flat", lineWidth: 0.01 })
      );

      const self = this;
      const updateEarthRotation = () => {
        if (!self.earthRotating) return;
        earthRotatingGroup.rotation.y += 0.008;
      };

      animations.push({
        update: updateEarthRotation,
        name: "earthRotationAnimation",
      });

      return earth;
    },

    // 创建星空背景
    createStarfield() {
      stars = ThreeUtils.createStarfield(8000);

      const starGroup = new THREE.Group();
      starGroup.add(stars);
      scene.add(starGroup);
      scene.background = new THREE.Color(0x000000);

      const animateStars = () => {
        starGroup.rotation.y += 0.0002;
      };
      animations.push({ update: animateStars });
    },

    // 添加光源
    initLight() {
      const ambientLight = new THREE.AmbientLight(0xcccccc, 1.1);
      scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.2);
      directionalLight.position.set(1, 0.1, 0).normalize();
      const directionalLight2 = new THREE.DirectionalLight(0xff2ffff, 0.2);
      directionalLight2.position.set(1, 0.1, 0.1).normalize();

      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.2);
      hemiLight.position.set(0, 1, 0);

      const mainLight = new THREE.DirectionalLight(0xffffff);
      mainLight.position.set(1, 500, -20);
      mainLight.castShadow = true;
      mainLight.shadow.camera.top = 18;
      mainLight.shadow.camera.bottom = -10;
      mainLight.shadow.camera.left = -52;
      mainLight.shadow.camera.right = 12;
      scene.add(directionalLight, directionalLight2, hemiLight, mainLight);
    },

    addLights() {
      const ambientLight = new THREE.AmbientLight(0xffffff, 1.1);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(10, 10, 5);
      scene.add(directionalLight);

      const pointLight = new THREE.PointLight(0x00ffff, 2, 50);
      pointLight.position.set(0, 5, 0);
      scene.add(pointLight);
    },

    // 初始化场景
    initScene() {
      this.cleanupScene();

      this.loading = true;
      animations = [];

      const containerEl = this.$refs.container;

      if (renderer && containerEl && containerEl.contains(renderer.domElement)) {
        containerEl.removeChild(renderer.domElement);
      }

      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x020924);

      camera = new THREE.PerspectiveCamera(
        cameraConfig.fov,
        containerEl.clientWidth / containerEl.clientHeight,
        cameraConfig.near,
        cameraConfig.far
      );
      camera.position.set(0, 10, 20);

      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        logarithmicDepthBuffer: true,
      });
      renderer.setSize(containerEl.clientWidth, containerEl.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      containerEl.appendChild(renderer.domElement);

      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableRotate = true;
      // 3D 下禁用平移，拖拽只绕球心旋转；切到 2D 平面时在 toggleViewMode 中开启
      controls.enablePan = false;
      controls.minDistance = 8;
      controls.maxDistance = 50;

      this.createStarfield();
      this.createEarth();
      this.initLight();
      this.addLights();

      this.animate();
      this.loading = false;
    },

    //地球缩小
    zoomIn() {
      camera.position.z *= 0.9;
    },
    //地球放大
    zoomOut() {
      camera.position.z *= 1.25;
    },

    toggleAnimation() {
      this.animationEnabled = !this.animationEnabled;
      if (animationId) {
        this.animate();
      }
    },

    toggleEarthRotation() {
      this.earthRotating = !this.earthRotating;
    },

    // 恢复地球自转（关闭项目列表面板时由父组件调用）
    resumeEarthRotation() {
      this.earthRotating = true;
    },

    // 切换 3D 地球 / 2D 平面地图
    toggleViewMode() {
      const containerEl = this.$refs.container;
      if (!camera || !controls || !containerEl || !earthRotatingGroup || !flatEarth) return;

      const w = containerEl.clientWidth;
      const h = containerEl.clientHeight;
      const aspect = w / h;

      if (this.viewMode === "3d") {
        // 切换到 2D 平面地图：隐藏球体，显示平面及平面数据流
        this.viewMode = "2d";
        earthRotatingGroup.visible = false;
        flatEarth.visible = true;
        if (flatFlowLines) flatFlowLines.visible = true;
        flatEarth.rotation.set(0, 0, 0);
        flatEarth.position.set(0, 0, 0);

        const frustum = 12;
        camera = new THREE.OrthographicCamera(
          -frustum * aspect,
          frustum * aspect,
          frustum,
          -frustum,
          0.1,
          1000
        );
        camera.position.set(0, 0, 35);
        camera.lookAt(0, 0, 0);
        controls.object = camera;
        controls.target.set(0, 0, 0);
        controls.enableRotate = false;
        controls.enablePan = true;
        controls.enableZoom = true;
        controls.screenSpacePanning = true;
        // 平面模式左键/单指拖拽用于平移（默认左键是旋转，关闭后会失效）
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.PAN,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.PAN,
        };
        controls.touches = {
          ONE: THREE.TOUCH.PAN,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
        controls.update();
      } else {
        // 切换回 3D 球体：隐藏平面及平面数据流，显示球体
        this.viewMode = "3d";
        flatEarth.visible = false;
        if (flatFlowLines) flatFlowLines.visible = false;
        earthRotatingGroup.visible = true;

        camera = new THREE.PerspectiveCamera(
          cameraConfig.fov,
          aspect,
          cameraConfig.near,
          cameraConfig.far
        );
        camera.position.set(0, 10, 20);
        camera.lookAt(0, 0, 0);
        controls.object = camera;
        // 重置控制器目标点到球心：清掉 2D 平移产生的偏移，保证地球居中、拖拽绕球心
        controls.target.set(0, 0, 0);
        controls.enableRotate = true;
        // 3D 下禁用平移，拖拽只绕球心旋转，避免地球被拖离画布中心
        controls.enablePan = false;
        // 还原 3D 模式按键：左键旋转（平移已禁用）
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.ROTATE,
        };
        controls.touches = {
          ONE: THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
        controls.update();
      }
    },

    // 动画循环
    animate() {
      animationId = requestAnimationFrame(this.animate.bind(this));
      controls.update();

      animations.forEach((animation) => {
        if (typeof animation.update === "function") {
          animation.update();
        }
      });

      renderer.render(scene, camera);
    },

    cleanupScene() {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }

      if (signalIntervalId) {
        clearInterval(signalIntervalId);
        signalIntervalId = null;
      }

      animations.length = 0;

      if (scene) {
        while (scene.children.length > 0) {
          const child = scene.children[0];
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((m) => m.dispose());
            } else {
              child.material.dispose();
            }
          }
          scene.remove(child);
        }
      }

      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        const containerEl = this.$refs.container;
        if (containerEl && containerEl.contains(renderer.domElement)) {
          containerEl.removeChild(renderer.domElement);
        }
      }

      earth = null;
      stars = null;
      earthRotatingGroup = null;
      flatEarth = null;
      flatFlowLines = null;
    },

    onWindowResize() {
      if (!camera || !renderer) return;
      const containerEl = this.$refs.container;
      const w = containerEl.clientWidth;
      const h = containerEl.clientHeight;
      if (this.viewMode === "2d") {
        const aspect = w / h;
        const frustum = 12;
        camera.left = -frustum * aspect;
        camera.right = frustum * aspect;
        camera.top = frustum;
        camera.bottom = -frustum;
      } else {
        camera.aspect = w / h;
      }
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    },

    triggerFlyLineAndPrizePanel() {},
  },
};
</script>

<style lang="scss" scoped>
.map-wrap {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
  overflow: hidden;
}

.map-container canvas {
  display: block;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #04f3ee;
  font-size: 24px;
  z-index: 10;
}
</style>
